import { Box, IconButton, Stack, Typography } from "@mui/material";
import { Flex, HStack, colors } from "@zfp-akademie-a-s/zfp-design";
import { CloseIcon, UploadIcon } from "@zfp-akademie-a-s/zfp-design/icons";
import { VideoIcon } from "Components/_Shared/Icons/Icons";
import { Radius, Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useEffect, useState } from "react";
import { ErrorCode, type FileError, type FileRejection, useDropzone } from "react-dropzone";
import {
  REPORT_ISSUE_MAX_ATTACHMENTS,
  REPORT_ISSUE_MAX_IMAGE_SIZE_MB,
  REPORT_ISSUE_MAX_TOTAL_SIZE_MB,
  REPORT_ISSUE_MAX_VIDEO_SIZE_MB,
} from "../../constants";

const BYTES_IN_MB = 1024 * 1024;

const ACCEPTED_FILE_TYPES = {
  "image/png": [".png"],
  "image/jpeg": [".jpg", ".jpeg"],
  "image/webp": [".webp"],
  "video/mp4": [".mp4"],
  "video/webm": [".webm"],
  "video/quicktime": [".mov"],
};

const isVideo = (file: { type: string }) => file.type.startsWith("video/");

type Props = {
  files: File[];
  onChange: (files: File[]) => void;
  disabled?: boolean;
};

export const ReportIssueAttachments = ({ files, onChange, disabled }: Props) => {
  const { t } = useResource();
  const [errors, setErrors] = useState<string[]>([]);

  const isFull = files.length >= REPORT_ISSUE_MAX_ATTACHMENTS;

  const validator = (file: File): FileError | null => {
    const video = isVideo(file);
    const limitMb = video ? REPORT_ISSUE_MAX_VIDEO_SIZE_MB : REPORT_ISSUE_MAX_IMAGE_SIZE_MB;

    if (file.size > limitMb * BYTES_IN_MB) {
      return {
        code: ErrorCode.FileTooLarge,
        message: t(
          video
            ? Resources.AnalysisDetail.ReportIssue.Attachments.Errors.VideoTooLarge
            : Resources.AnalysisDetail.ReportIssue.Attachments.Errors.ImageTooLarge,
          { name: file.name, size: limitMb },
        ),
      };
    }
    return null;
  };

  // Count and total-size limits depend on files already attached, so they are checked here rather than in the dropzone config.
  const onDrop = (acceptedFiles: File[], rejections: FileRejection[]) => {
    const nextErrors = rejections.map(({ file, errors: fileErrors }) =>
      fileErrors[0]?.code === ErrorCode.FileInvalidType
        ? t(Resources.AnalysisDetail.ReportIssue.Attachments.Errors.InvalidType, { name: file.name })
        : (fileErrors[0]?.message ?? ""),
    );

    const nextFiles = [...files];
    let totalSize = nextFiles.reduce((sum, file) => sum + file.size, 0);

    for (const file of acceptedFiles) {
      if (nextFiles.length >= REPORT_ISSUE_MAX_ATTACHMENTS) {
        nextErrors.push(t(Resources.AnalysisDetail.ReportIssue.Attachments.Errors.TooMany, { count: REPORT_ISSUE_MAX_ATTACHMENTS }));
        continue;
      }
      if (totalSize + file.size > REPORT_ISSUE_MAX_TOTAL_SIZE_MB * BYTES_IN_MB) {
        nextErrors.push(t(Resources.AnalysisDetail.ReportIssue.Attachments.Errors.TotalTooLarge, { size: REPORT_ISSUE_MAX_TOTAL_SIZE_MB }));
        continue;
      }
      nextFiles.push(file);
      totalSize += file.size;
    }

    setErrors([...new Set(nextErrors)]);
    onChange(nextFiles);
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    validator,
    accept: ACCEPTED_FILE_TYPES,
    multiple: true,
    disabled: disabled || isFull,
  });

  const removeFile = (index: number) => {
    setErrors([]);
    onChange(files.filter((_, i) => i !== index));
  };

  return (
    <Stack spacing={Spacing.M}>
      <Flex
        {...getRootProps()}
        flexDirection="column"
        alignItems="center"
        gap={Spacing.S}
        p={Spacing.XL}
        borderRadius={Radius.M}
        border="1px dashed"
        borderColor={isDragActive ? "primary.main" : "divider"}
        bgcolor={isDragActive ? "primary.light" : "transparent"}
        textAlign="center"
        sx={{
          cursor: disabled || isFull ? "default" : "pointer",
          opacity: disabled || isFull ? 0.5 : 1,
          outline: "none",
          "&:focus-visible": { borderColor: "primary.main" },
        }}
      >
        <input {...getInputProps()} />
        <UploadIcon sx={{ color: "primary.main" }} />
        <Typography variant="body2" fontWeight={600}>
          {t(Resources.AnalysisDetail.ReportIssue.Attachments.Title)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          {t(Resources.AnalysisDetail.ReportIssue.Attachments.Description, {
            count: REPORT_ISSUE_MAX_ATTACHMENTS,
            imageSize: REPORT_ISSUE_MAX_IMAGE_SIZE_MB,
            videoSize: REPORT_ISSUE_MAX_VIDEO_SIZE_MB,
            totalSize: REPORT_ISSUE_MAX_TOTAL_SIZE_MB,
          })}
        </Typography>
      </Flex>

      {errors.map((error) => (
        <Typography key={error} variant="caption" color="error.main">
          {error}
        </Typography>
      ))}

      {files.length > 0 && (
        <HStack spacing={Spacing.M}>
          {files.map((file, index) => (
            <AttachmentPreview
              key={`${file.name}-${file.size}-${file.lastModified}`}
              file={file}
              onRemove={() => removeFile(index)}
              disabled={disabled}
            />
          ))}
        </HStack>
      )}
    </Stack>
  );
};

type AttachmentPreviewProps = {
  file: File;
  onRemove: () => void;
  disabled?: boolean;
};

const AttachmentPreview = ({ file, onRemove, disabled }: AttachmentPreviewProps) => {
  const { t } = useResource();
  const [previewUrl, setPreviewUrl] = useState<string>();

  useEffect(() => {
    if (isVideo(file)) return;

    const url = URL.createObjectURL(file);
    setPreviewUrl(url);
    return () => URL.revokeObjectURL(url);
  }, [file]);

  return (
    <Box
      position="relative"
      width={96}
      height={96}
      borderRadius={Radius.M}
      border="1px solid"
      borderColor="divider"
      bgcolor={colors.backgroundLight}
      overflow="hidden"
      title={file.name}
    >
      {previewUrl ? (
        <Box component="img" src={previewUrl} alt={file.name} width="100%" height="100%" sx={{ objectFit: "cover" }} />
      ) : (
        <Flex flexDirection="column" alignItems="center" justifyContent="center" height="100%" px={Spacing.S}>
          <VideoIcon sx={{ color: "grey.400" }} />
          <Typography variant="caption" noWrap maxWidth="100%">
            {file.name}
          </Typography>
        </Flex>
      )}
      <IconButton
        size="small"
        aria-label={t(Resources.AnalysisDetail.ReportIssue.Attachments.Remove)}
        onClick={onRemove}
        disabled={disabled}
        sx={{
          position: "absolute",
          top: 4,
          right: 4,
          p: Spacing.XS,
          bgcolor: "background.paper",
          "&:hover": { bgcolor: colors.backgroundSecondary },
          "& svg": { width: 12, height: 12 },
        }}
      >
        <CloseIcon sx={{ color: "text.primary" }} />
      </IconButton>
    </Box>
  );
};
